import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, ThumbsUp, ThumbsDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  findBestFAQ,
  detectIntent,
  generateContextualResponse,
  getQuickReplies,
  validateUserMessage,
  type ChatResponse,
} from '../lib/chatbot-utils';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  faqId?: string;
  source?: 'faq' | 'context' | 'default' | 'api';
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
}

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'bot',
      content: 'Bonjour! Je suis l\'assistant Nova Communication. Comment puis-je vous aider?',
      timestamp: new Date(),
      source: 'default',
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Generate unique user ID for anonymous tracking
  const [userId] = useState(() => {
    let id = localStorage.getItem('chatbot_user_id');
    if (!id) {
      id = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chatbot_user_id', id);
    }
    return id;
  });

  // Load FAQs from database
  useEffect(() => {
    const loadFAQs = async () => {
      const { data, error } = await supabase
        .from('faqs')
        .select('id, question, answer, order')
        .order('order', { ascending: true });

      if (!error && data) {
        setFaqs(data as FAQ[]);
      }
    };

    loadFAQs();
  }, []);

  // Initialize or load conversation
  useEffect(() => {
    const initializeConversation = async () => {
      if (!conversationId) {
        const { data, error } = await supabase
          .from('chat_conversations')
          .insert([{ user_id: userId, messages: [] }])
          .select()
          .maybeSingle();

        if (!error && data) {
          setConversationId(data.id);
        }
      }
    };

    initializeConversation();
  }, [userId, conversationId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update quick replies
  useEffect(() => {
    const replies = getQuickReplies(messages.length, messages[messages.length - 1]?.content || '');
    setQuickReplies(replies);
  }, [messages]);

  const getBotResponse = useCallback(
    async (userMessage: string): Promise<ChatResponse> => {
      // 1. Try local FAQ system first
      if (faqs.length > 0) {
        const intent = detectIntent(userMessage);
        const bestFAQ = findBestFAQ(userMessage, faqs);

        // If FAQ match found, return immediately
        if (bestFAQ && bestFAQ.score > 0.2) {
          return {
            content: bestFAQ.faq.answer,
            source: 'faq',
            faqId: bestFAQ.faq.id,
          };
        }

        // If specific intent detected (not general), return contextual response
        if (intent !== 'general') {
          return generateContextualResponse(userMessage, intent, null);
        }
      }

      // 2. Fallback to API if no local match or general intent
      try {
        const conversationHistory = messages
          .slice(-10)
          .map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.content,
          }));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              message: userMessage,
              conversationHistory,
            }),
            signal: controller.signal,
          }
        );
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        return {
          content: data.content,
          source: 'claude' as const,
        };
      } catch (error) {
        console.error('Chat API error:', error);
        // Final fallback to local contextual response
        const intent = detectIntent(userMessage);
        return generateContextualResponse(userMessage, intent, null);
      }
    },
    [messages, faqs]
  );

  const saveInteraction = async (
    messageText: string,
    responseText: string,
    source: 'faq' | 'context' | 'default' | 'api'
  ) => {
    if (!conversationId) return;

    await supabase.from('chat_interactions').insert([
      {
        conversation_id: conversationId,
        message_text: messageText,
        response_text: responseText,
        response_source: source,
      },
    ]);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateUserMessage(inputValue);
    if (!validation.valid) return;

    const userInput = inputValue;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: userInput,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setFeedbackId(null);

    try {
      const response = await getBotResponse(userInput);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: response.content,
        timestamp: new Date(),
        source: response.source as 'faq' | 'context' | 'default' | 'api',
        faqId: response.faqId,
      };

      setMessages(prev => [...prev, botMessage]);
      setFeedbackId(botMessage.id);

      // Save interaction asynchronously
      saveInteraction(userInput, response.content, response.source);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: 'Désolé, une erreur est survenue. Veuillez réessayer.',
        timestamp: new Date(),
        source: 'default',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (reply: string) => {
    setInputValue(reply);
  };

  const handleFeedback = async (interactionId: string, helpful: boolean) => {
    if (!conversationId) return;

    await supabase
      .from('chat_interactions')
      .update({ helpful })
      .eq('id', interactionId);

    setFeedbackId(null);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-20 right-4 w-96 max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-8rem)] bg-gray-900 border border-blue-500/30 rounded-lg shadow-2xl flex flex-col z-40"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gradient-to-r from-gray-900 to-gray-800">
              <div>
                <h3 className="font-semibold text-white">Nova Assistant</h3>
                <p className="text-xs text-gray-400">Online</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex flex-col ${message.type === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-xs px-4 py-3 rounded-lg text-sm ${
                      message.type === 'user'
                        ? 'bg-blue-500 text-white rounded-br-none'
                        : 'bg-gray-800 text-gray-100 rounded-bl-none'
                    }`}
                  >
                    <p className="break-words">{message.content}</p>
                  </div>

                  {message.type === 'bot' && message.id === feedbackId && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex gap-2 mt-2 text-xs"
                    >
                      <button
                        onClick={() => handleFeedback(message.id, true)}
                        className="flex items-center gap-1 text-gray-400 hover:text-green-400 transition-colors"
                        title="Utile"
                      >
                        <ThumbsUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleFeedback(message.id, false)}
                        className="flex items-center gap-1 text-gray-400 hover:text-red-400 transition-colors"
                        title="Pas utile"
                      >
                        <ThumbsDown className="w-3 h-3" />
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              ))}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-800 rounded-lg rounded-bl-none px-4 py-3">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ y: [0, -8, 0] }}
                          transition={{ duration: 0.6, delay: i * 0.1, repeat: Infinity }}
                          className="w-2 h-2 bg-blue-400 rounded-full"
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {!isLoading && quickReplies.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-700 flex gap-2 flex-wrap">
                {quickReplies.map((reply) => (
                  <button
                    key={reply}
                    onClick={() => handleQuickReply(reply)}
                    className="text-xs px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-full transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Écrivez votre message..."
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-sm"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="p-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 w-14 h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all z-40"
      >
        <MessageSquare className="w-6 h-6" />
      </motion.button>
    </>
  );
}
